import {
  EElectricityMeterType,
  EElectricityType,
  EHeatingType,
  ENaturalGasAvailable,
  ENaturalGasMeterType,
  EOrientation,
  EPropertyAdjoined,
  EPropertyCondition,
  EPropertyElectricityCertificate,
  EPropertyEnvironmentType,
  EPropertyFloodScore,
  EPropertyFloorType,
  EPropertyGlazingType,
  EPropertyHeatingDistribution,
  EPropertyKitchenType,
  EPropertyPetsAllowedDetail,
  EPropertyRoofMaterial,
  EPropertyRoofType,
  EPropertyWindowFrameMaterial,
  EYesNoUnknown,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

// The enum members below are copied from api-next so the wire value and the
// name stay identical; three of them are not English words.
// cSpell:ignore Arei Aluminium Epdm

/** The value lists of the enums this tab offers, in the order they are shown.
 * Each one is translated under its own prefix, with the enum's own value
 * lowercased as the last part of the key, so the wire value and the label
 * never drift apart.
 *
 * `property.option.*` is shared with Rentmatch: the same value is named once
 * and both screens read it, rather than each app keeping a word of its own for
 * "Parquet". The heating list keeps the older `property.extra.option.energy`
 * prefix because six of its values were already translated there; the four
 * from the CRM list join them instead of starting a second set. The rest point
 * at translations that predate this tab. */
export const OPTION_SETS = {
  yesNoUnknown: {
    prefix: "property.option.yes_no_unknown",
    ids: [EYesNoUnknown.Yes, EYesNoUnknown.No, EYesNoUnknown.Unknown],
  },
  orientation: {
    prefix: "property.option.orientation",
    ids: [
      EOrientation.North,
      EOrientation.NorthEast,
      EOrientation.East,
      EOrientation.SouthEast,
      EOrientation.South,
      EOrientation.SouthWest,
      EOrientation.West,
      EOrientation.NorthWest,
    ],
  },
  kitchenType: {
    prefix: "property.option.kitchen_type",
    ids: [
      EPropertyKitchenType.NotFitted,
      EPropertyKitchenType.SemiFitted,
      EPropertyKitchenType.FullyFitted,
      EPropertyKitchenType.HyperEquipped,
      EPropertyKitchenType.UsSemiFitted,
      EPropertyKitchenType.UsFullyFitted,
      EPropertyKitchenType.UsHyperEquipped,
    ],
  },
  floorType: {
    prefix: "property.option.floor_type",
    ids: [
      EPropertyFloorType.Parquet,
      EPropertyFloorType.Tiled,
      EPropertyFloorType.Carpet,
      EPropertyFloorType.Laminate,
      EPropertyFloorType.Vinyl,
      EPropertyFloorType.Concrete,
      EPropertyFloorType.NaturalStone,
      EPropertyFloorType.Wood,
      EPropertyFloorType.Other,
    ],
  },
  heatingType: {
    prefix: "property.extra.option.energy",
    ids: [
      EHeatingType.NaturalGas,
      EHeatingType.Electricity,
      EHeatingType.HeatPump,
      EHeatingType.FuelOil,
      EHeatingType.Coal,
      EHeatingType.GasBoiler,
      EHeatingType.CondensingGasBoiler,
      EHeatingType.Underfloor,
      EHeatingType.District,
      EHeatingType.Other,
    ],
  },
  heatingDistribution: {
    prefix: "property.option.heating_distribution",
    ids: [
      EPropertyHeatingDistribution.Individual,
      EPropertyHeatingDistribution.Collective,
    ],
  },
  glazingType: {
    prefix: "property.option.glazing_type",
    ids: [
      EPropertyGlazingType.Single,
      EPropertyGlazingType.Double,
      EPropertyGlazingType.Triple,
      EPropertyGlazingType.Mixed,
    ],
  },
  windowFrameMaterial: {
    prefix: "property.option.window_frame_material",
    ids: [
      EPropertyWindowFrameMaterial.Wood,
      EPropertyWindowFrameMaterial.Pvc,
      EPropertyWindowFrameMaterial.Aluminium,
      EPropertyWindowFrameMaterial.Mixed,
    ],
  },
  petsAllowedDetail: {
    prefix: "property.option.pets_allowed_detail",
    ids: [
      EPropertyPetsAllowedDetail.SmallPets,
      EPropertyPetsAllowedDetail.LargePets,
      EPropertyPetsAllowedDetail.AllPets,
    ],
  },
  condition: {
    prefix: "property.option.condition",
    ids: [
      EPropertyCondition.NewBuild,
      EPropertyCondition.ReadyToMoveIn,
      EPropertyCondition.Renovated,
      EPropertyCondition.ToRefresh,
      EPropertyCondition.ToRenovate,
      EPropertyCondition.ToRestore,
    ],
  },
  environmentType: {
    prefix: "property.option.environment_type",
    ids: [
      EPropertyEnvironmentType.Residential,
      EPropertyEnvironmentType.Quiet,
      EPropertyEnvironmentType.CityCenter,
      EPropertyEnvironmentType.Rural,
    ],
  },
  roofType: {
    prefix: "property.option.roof_type",
    ids: [
      EPropertyRoofType.Flat,
      EPropertyRoofType.Gable,
      EPropertyRoofType.Butterfly,
      EPropertyRoofType.NorthLight,
    ],
  },
  roofMaterial: {
    prefix: "property.option.roof_material",
    ids: [
      EPropertyRoofMaterial.Tiles,
      EPropertyRoofMaterial.NaturalSlate,
      EPropertyRoofMaterial.ArtificialSlate,
      EPropertyRoofMaterial.Bitumen,
      EPropertyRoofMaterial.Epdm,
      EPropertyRoofMaterial.Other,
    ],
  },
  electricityCertificate: {
    prefix: "property.option.electricity_certificate",
    ids: [
      EPropertyElectricityCertificate.CompliantArei,
      EPropertyElectricityCertificate.NonCompliantArei,
      EPropertyElectricityCertificate.Pending,
      EPropertyElectricityCertificate.NotRequested,
    ],
  },
  floodScore: {
    prefix: "property.option.flood_score",
    ids: [
      EPropertyFloodScore.ClassA,
      EPropertyFloodScore.ClassB,
      EPropertyFloodScore.ClassC,
      EPropertyFloodScore.ClassD,
    ],
  },
  adjoined: {
    prefix: "property.adjoined.option",
    suffix: ".label",
    ids: [
      EPropertyAdjoined.Freestanding,
      EPropertyAdjoined.OneSide,
      EPropertyAdjoined.BothSides,
      EPropertyAdjoined.Unknown,
    ],
  },
  electricityType: {
    prefix: "move.option.electricity_type",
    ids: [
      EElectricityType.Single,
      EElectricityType.Double,
      EElectricityType.Unknown,
    ],
  },
  electricityMeterType: {
    prefix: "move.option.electricity_meter_type",
    ids: [
      EElectricityMeterType.Standard,
      EElectricityMeterType.Opening,
      EElectricityMeterType.Budget,
      EElectricityMeterType.Empty,
    ],
  },
  naturalGasAvailable: {
    prefix: "move.option.natural_gas_type",
    ids: [
      ENaturalGasAvailable.Available,
      ENaturalGasAvailable.Unavailable,
      ENaturalGasAvailable.Unknown,
    ],
  },
  naturalGasMeterType: {
    prefix: "move.option.natural_gas_meter_type",
    ids: [
      ENaturalGasMeterType.Standard,
      ENaturalGasMeterType.Opening,
      ENaturalGasMeterType.Budget,
      ENaturalGasMeterType.Empty,
    ],
  },
} as const;

export type TOptionSet = keyof typeof OPTION_SETS;

export const optionsForSet = (set: TOptionSet) => {
  const definition: {
    prefix: string;
    suffix?: string;
    ids: readonly string[];
  } = OPTION_SETS[set];
  const { prefix, suffix = "", ids } = definition;

  return ids.map(id => ({
    id,
    value: getLocalizedText(`${prefix}.${id}${suffix}`.toLowerCase()),
  }));
};

/** The label of one stored value, for the read-only card behind the form. An
 * unknown value is shown as it was stored rather than dropped: a CRM that
 * sends something outside the list is a thing to notice, not to hide. */
export const getOptionLabel = (
  set: TOptionSet,
  value?: string | number | boolean | null,
) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const match = optionsForSet(set).find(option => option.id === value);
  return match ? match.value : `${value}`;
};

/** The translation key of one stored value, without translating it. The
 * document editor needs the key rather than the label, because it renders in
 * the language of the document being produced and not in the one the broker
 * happens to be looking at. */
export const getOptionTranslationKey = (
  set: TOptionSet,
  value: string,
): string => {
  const definition: {
    prefix: string;
    suffix?: string;
    ids: readonly string[];
  } = OPTION_SETS[set];

  // Only the value is lowercased, not the whole key: the prefix and suffix are
  // written here and are already correct, and lowercasing them too would
  // quietly repair a typo instead of letting it fail (CLAUDE.md). The value is
  // an enum's wire form, which does arrive uppercase.
  return `${definition.prefix}.${value.toLowerCase()}${
    definition.suffix || ""
  }`;
};
